import { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { getUserStatusVariant } from "@vega-api/custom/utils/getUserStatusVariant";
import { usePagination } from "@vega-api/custom/utils/usePagination";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import {
  UiCapabilityKey,
  useUiCapabilities,
} from "@vega-api/extended/auth/useUiCapabilities";
import { useGetWorkbenchCollaborators } from "@vega-api/generated/auth-workbench-v1/auth-workbench-v1";
import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import { type Meta } from "@vega-api/generated/types/meta";
import { type SubjectType } from "@vega-api/generated/types/subjectType";
import {
  UserStatus,
  type UserStatus as UserStatusType,
} from "@vega-api/generated/types/userStatus";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { InteractiveButton } from "@vega-core/components/Buttons/InteractiveButton";
import { ACTION_COLUMN_BUTTON_DEFAULT_PROPS } from "@vega-core/components/Table/CoreTable/constants";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { m } from "@vega-core/paraglide/messages";
import { formatShortSlashDate } from "@vega-core/utils/formatDate";
import {
  camelCaseToCapitalize,
  convertToTitleCase,
  formatFullName,
} from "@vega-core/utils/formatString";
import { useTableFilters } from "@vega-modules/components/Table/TableFilters/hooks/useTableFilters";
import {
  FilterType,
  TableFilterId,
  type TableFiltersConfig,
} from "@vega-modules/components/Table/TableFilters/types";

import {
  COLLABORATOR_FUND_SUBJECT_TYPES,
  CollaboratorRolesCell,
} from "../components/collaboratorsV2Constants";

export type CollaboratorResendUser = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
};

export const useCollaboratorsTableFilters = (
  onResendInvite: (user: CollaboratorResendUser) => void,
) => {
  const {
    data: { data: collaboratorsData } = {},
    isLoading: isCollaboratorsLoading,
  } = useGetWorkbenchCollaborators();

  const { featureFlag } = useFeatureFlag();
  const { capabilities } = useUiCapabilities();
  const isCollaboratorsV2 = featureFlag[FeatureFlag.GP_COLLABORATORS_V2];

  const showResendColumn =
    featureFlag.gp_settings_invite_collaborators &&
    capabilities[UiCapabilityKey.CanManageWorkbenchCollaborators];

  const collaboratorsRoles = useMemo(() => {
    if (!collaboratorsData) return [];

    const roles = collaboratorsData.reduce<
      {
        roleName: string | undefined;
        roleId: string | undefined;
        subjectType: SubjectType | undefined;
      }[]
    >((allRoles, collaborator) => {
      const fundRoles = isCollaboratorsV2
        ? collaborator.permissions?.filter(
            (role) =>
              role.subjectType &&
              COLLABORATOR_FUND_SUBJECT_TYPES.includes(role.subjectType),
          ) || []
        : collaborator.permissions || [];

      fundRoles.forEach((role) => {
        allRoles.push({
          roleName: role.roleName,
          roleId: role.roleId,
          subjectType: role.subjectType,
        });
      });

      return allRoles;
    }, []);

    // Deduplicate by roleId (keeps last occurrence)
    return roles.length > 0
      ? Array.from(new Map(roles.map((role) => [role.roleId, role])).values())
      : [];
  }, [collaboratorsData, isCollaboratorsV2]);

  const collaboratorsStatuses = useMemo(() => {
    if (!collaboratorsData) return [];

    const statuses = Array.from(
      new Set(
        collaboratorsData
          .map((collaborator) => collaborator.status)
          .filter(Boolean),
      ),
    );

    return statuses.length > 0 ? statuses : [];
  }, [collaboratorsData]);

  const rolesOptions = useMemo(
    () =>
      collaboratorsRoles.map((role) => ({
        label: convertToTitleCase(role.roleName) || "",
        value: role.roleName || "",
        id: role.roleId || "",
      })),
    [collaboratorsRoles],
  );

  const statusesOptions = useMemo(
    () =>
      collaboratorsStatuses.map((status) => ({
        label: camelCaseToCapitalize(status),
        value: status,
      })),
    [collaboratorsStatuses],
  );

  const filtersConfig = {
    maxShownFilters: 3,
    filters: [
      {
        id: "searchQuery",
        label: m.settings_searchUsers(),
        type: FilterType.Search,
        props: {
          placeholder: m.common_searchCollaborators(),
        },
      },
      {
        id: "roles",
        label: m.common_role(),
        type: FilterType.Multiselect,
        props: {
          placeholder: m.common_allRoles(),
          options: rolesOptions,
          searchEnabled: true,
          hideSelectAllOption: true,
          contentWrapperClassName: "min-w-64",
        },
      },
      {
        id: "statuses",
        label: m.common_status(),
        type: FilterType.Multiselect,
        props: {
          placeholder: m.common_allStatuses(),
          options: statusesOptions,
          searchEnabled: true,
          hideSelectAllOption: true,
          contentWrapperClassName: "min-w-48",
        },
      },
    ],
    showResetButton: true,
  } as const satisfies TableFiltersConfig;

  const { filterState, RenderFilters, hasActiveFilters } = useTableFilters({
    id: TableFilterId.Collaborators,
    config: filtersConfig,
    isLoading: isCollaboratorsLoading,
    syncWithURL: false,
    showSavedFilters: false,
  });

  const filteredCollaborators = useMemo(() => {
    if (!collaboratorsData) return [];

    const { searchQuery, roles = [], statuses = [] } = filterState;

    return collaboratorsData.filter((collaborator) => {
      // Search filter
      if (searchQuery) {
        const fullName = formatFullName({
          firstName: collaborator.firstName,
          lastName: collaborator.lastName,
          middleName: collaborator.middleName,
        }).toLowerCase();
        const email = collaborator.email.toLowerCase();
        const lastActivity = formatShortSlashDate(
          collaborator.lastActivity,
        ).toLowerCase();

        const query = searchQuery.toLowerCase();

        const matchesSearch =
          fullName.includes(query) ||
          email.includes(query) ||
          lastActivity.includes(query);

        if (!matchesSearch) return false;
      }

      // Role filter
      if (roles.length > 0) {
        const hasSelectedRole = roles.some((role) =>
          collaborator.permissions?.some(
            (permission) => permission.roleName === role.value,
          ),
        );

        if (!hasSelectedRole) return false;
      }

      // Status filter
      if (statuses.length > 0) {
        const hasSelectedStatus = statuses.some(
          (status) => collaborator.status === status.value,
        );

        if (!hasSelectedStatus) return false;
      }

      return true;
    });
  }, [collaboratorsData, filterState]);

  const paginationState = usePaginationState();
  const { currentPage, currentPageSize, setCurrentPage, setCurrentPageSize } =
    paginationState;
  const totalCount = filteredCollaborators.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / currentPageSize));

  const paginationMeta = useMemo<Meta>(
    () => ({
      count: totalCount,
      page: currentPage,
      pageSize: currentPageSize,
      totalPages,
      hasNextPage: currentPage < totalPages,
    }),
    [totalCount, currentPage, currentPageSize, totalPages],
  );

  const { paginationElement } = usePagination({
    meta: paginationMeta,
    isLoading: isCollaboratorsLoading,
    currentPage,
    setCurrentPage,
    currentPageSize,
    setCurrentPageSize,
    filterDependencies: [filterState],
  });

  const paginatedCollaborators = useMemo(() => {
    const start = (currentPage - 1) * currentPageSize;

    return filteredCollaborators.slice(start, start + currentPageSize);
  }, [filteredCollaborators, currentPage, currentPageSize]);

  const collaboratorsColumns: ColumnDef<CollaboratorDetails>[] = [
    {
      header: m.common_name(),
      accessorKey: "name",
      size: 300,
      minSize: 300,
      cell: ({ row }) => (
        <div className="flex gap-3 items-center truncate">
          <Avatar
            initials={
              row.original.firstName
                ? row.original.firstName[0]
                : row.original.email[0]
            }
          />
          <div className="flex flex-col">
            <div className="capitalize body-base-medium text-text/primary">
              {formatFullName({
                firstName: row.original.firstName,
                lastName: row.original.lastName,
                middleName: row.original.middleName,
              })}
            </div>
            <div className="lowercase body-small-regular text-text/secondary">
              {row.original.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      header: m.common_phoneNumber(),
      accessorKey: "phone",
    },
    {
      header: m.common_role(),
      accessorKey: "role",
      size: 300,
      minSize: 300,
      cell: ({ row }) => (
        <CollaboratorRolesCell permissions={row.original.permissions} />
      ),
    },
    {
      header: m.common_status(),
      accessorKey: "status",
      align: "left",
      cell: (status) => {
        const statusValue = status.getValue<UserStatusType>();

        return (
          <Tag variant={getUserStatusVariant(statusValue)}>
            {convertToTitleCase(statusValue)}
          </Tag>
        );
      },
    },
    {
      header: m.common_workbenchAccess(),
      accessorKey: "workbenchPortalAccess",
      align: "left",
      cell: ({ row }) => {
        const hasAccess = (row.original as { workbenchPortalAccess?: boolean })
          .workbenchPortalAccess;

        return (
          <Tag
            variant={hasAccess ? TagVariants.Positive : TagVariants.Negative}
          >
            {hasAccess ? m.common_yes() : m.common_no()}
          </Tag>
        );
      },
    },
    {
      header: m.common_lastActive(),
      accessorKey: "last-active",
      cell: ({ row }) => (
        <span>
          {row.original.lastActivity
            ? formatShortSlashDate(row.original.lastActivity)
            : "-"}
        </span>
      ),
    },
  ];

  if (showResendColumn) {
    collaboratorsColumns.push({
      ...ACTION_COLUMN_BUTTON_DEFAULT_PROPS,
      cell: ({ row }) => {
        const { status, id, email, firstName, lastName } = row.original;

        if (status !== UserStatus.INVITED) return null;

        return (
          <InteractiveButton
            className="text-text/interactive no-underline hover:no-underline"
            dataTestId="resend-collaborator-invite-button"
            onClick={(event) => {
              event.stopPropagation();
              onResendInvite({ id, email, firstName, lastName });
            }}
            variant="text"
          >
            {m.userManagement_resend_invite_button()}
          </InteractiveButton>
        );
      },
    });
  }

  return {
    collaboratorsColumns,
    collaboratorsData,
    filteredCollaborators: paginatedCollaborators,
    isCollaboratorsLoading,
    RenderFilters,
    hasActiveFilters,
    rolesOptions,
    paginationElement,
  };
};
